import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, Send, X, Trash2, Bot, User, Loader2,
  Sparkles, Calendar, Users, Search, BarChart3, Mic, MicOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { chatAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const QUICK_ACTIONS = [
  { icon: Search, label: 'Zoek klant', prompt: 'Zoek klant ' },
  { icon: Calendar, label: 'Plan afspraak', prompt: 'Plan een afspraak voor ' },
  { icon: BarChart3, label: 'Statistieken', prompt: 'Toon de statistieken' },
  { icon: Users, label: 'Verkoopkansen', prompt: 'Welke verkoopkansen zijn er?' },
];

const MAX_MESSAGES = 40; // 20 user + 20 assistant messages
const STORAGE_KEY = 'solar_ai_chat';

const ChatMessage = ({ message, isUser }) => (
  <div className={cn(
    "flex gap-3 mb-4",
    isUser ? "flex-row-reverse" : "flex-row"
  )}>
    <div className={cn(
      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
      isUser ? "bg-primary" : "bg-purple-500/20"
    )}>
      {isUser ? (
        <User className="w-4 h-4 text-primary-foreground" />
      ) : (
        <Bot className="w-4 h-4 text-purple-400" />
      )}
    </div>
    <div className={cn(
      "max-w-[80%] rounded-lg px-4 py-2 text-sm",
      isUser 
        ? "bg-primary text-primary-foreground" 
        : "bg-secondary text-foreground"
    )}>
      <p className="whitespace-pre-wrap">{message.content}</p>
    </div>
  </div>
);

const AIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Spraak naar tekst (browser Web Speech API)
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const speechSupported = typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Spraakherkenning wordt niet ondersteund door deze browser (gebruik Chrome/Edge/Safari)');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'nl-BE';
    recognition.continuous = true;
    recognition.interimResults = true;
    let baseText = input;
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput((baseText ? baseText + ' ' : '') + transcript);
    };
    recognition.onerror = (e) => {
      if (e.error === 'not-allowed') toast.error('Geef microfoon-toestemming in je browser');
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();

  // Load saved conversation from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { messages: savedMessages, conversationId: savedConvId } = JSON.parse(saved);
        if (savedMessages && savedMessages.length > 0) {
          setMessages(savedMessages);
          setConversationId(savedConvId);
        }
      } catch (e) {
        console.error('Error loading saved chat:', e);
      }
    }
  }, []);

  // Save conversation to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages,
        conversationId
      }));
    }
  }, [messages, conversationId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    
    // Keep only last MAX_MESSAGES
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      if (newMessages.length > MAX_MESSAGES) {
        return newMessages.slice(-MAX_MESSAGES);
      }
      return newMessages;
    });
    
    setInput('');
    setLoading(true);

    try {
      const response = await chatAPI.send(messageText.trim(), conversationId);
      setConversationId(response.data.conversation_id);
      
      const assistantMessage = { role: 'assistant', content: response.data.response };
      
      // Keep only last MAX_MESSAGES
      setMessages(prev => {
        const newMessages = [...prev, assistantMessage];
        if (newMessages.length > MAX_MESSAGES) {
          return newMessages.slice(-MAX_MESSAGES);
        }
        return newMessages;
      });
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Er ging iets mis met de AI assistent');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, er ging iets mis. Probeer het opnieuw.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = async () => {
    if (conversationId) {
      try {
        await chatAPI.clearHistory(conversationId);
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleQuickAction = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  // Close chat without losing messages
  const handleClose = () => {
    setIsOpen(false);
  };

  if (!user || user.role === 'cleaner') return null;

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-4 sm:bottom-20 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg z-50",
          "bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400",
          isOpen && "hidden"
        )}
        data-testid="ai-chat-button"
      >
        <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed inset-4 sm:inset-auto sm:bottom-20 sm:right-6 sm:w-[400px] sm:h-[600px] max-h-[calc(100vh-2rem)] shadow-2xl z-50 flex flex-col border-purple-500/30" data-testid="ai-chat-window">
          {/* Header */}
          <CardHeader className="flex-shrink-0 border-b border-border/50 py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-purple-600 to-purple-400 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-sm sm:text-base truncate">Business Assistant AI</CardTitle>
                  <p className="text-xs text-muted-foreground truncate">Vraag me alles over je CRM</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={handleClearChat}
                  title="Wis gesprek"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClose}
                  title="Sluiten"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-muted-foreground mb-4">
                  Hallo {user?.first_name}! Hoe kan ik je helpen?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_ACTIONS.map((action, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      className="text-xs justify-start"
                      onClick={() => handleQuickAction(action.prompt)}
                    >
                      <action.icon className="w-3 h-3 mr-2" />
                      {action.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <ChatMessage key={i} message={msg} isUser={msg.role === 'user'} />
                ))}
                {loading && (
                  <div className="flex gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="bg-secondary rounded-lg px-4 py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="flex-shrink-0 border-t border-border/50 p-4">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={listening ? 'Aan het luisteren...' : 'Typ je vraag...'}
                disabled={loading}
                className="flex-1"
                data-testid="ai-chat-input"
              />
              <Button
                onClick={toggleListening}
                variant={listening ? 'destructive' : 'outline'}
                className={listening ? 'animate-pulse' : ''}
                title={listening ? 'Stop opname' : 'Spreek je vraag in'}
                data-testid="ai-chat-mic"
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 text-center">
              Powered by OpenAI GPT-4
            </p>
          </div>
        </Card>
      )}
    </>
  );
};

export default AIChat;
